import React, { useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Download, Link as LinkIcon, Share2, Copy, Check } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const QRCodePage = () => {
  const defaultUrl =
    typeof window !== 'undefined' ? window.location.origin : 'https://eliteiv.org';
  const [url, setUrl] = useState(defaultUrl);
  const [size, setSize] = useState(512);
  const [includeBranding, setIncludeBranding] = useState(true);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);

  // Generate a stylized PNG (QR + brand band) and trigger download
  const handleDownload = () => {
    const qrCanvas = canvasRef.current?.querySelector('canvas');
    if (!qrCanvas) return;

    if (!includeBranding) {
      const link = document.createElement('a');
      link.download = 'eliteiv-qr-code.png';
      link.href = qrCanvas.toDataURL('image/png');
      link.click();
      return;
    }

    // Compose branded canvas: dark BG + gold band on top, QR centered, footer text
    const compositeWidth = size + 80;
    const compositeHeight = size + 200;
    const composite = document.createElement('canvas');
    composite.width = compositeWidth;
    composite.height = compositeHeight;
    const ctx = composite.getContext('2d');

    // Black background
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, compositeWidth, compositeHeight);

    // Gold accent border
    ctx.strokeStyle = '#FFD700';
    ctx.lineWidth = 4;
    ctx.strokeRect(8, 8, compositeWidth - 16, compositeHeight - 16);

    // Brand heading
    ctx.fillStyle = '#FFD700';
    ctx.font = 'bold 42px serif';
    ctx.textAlign = 'center';
    ctx.fillText('EliteIV Lounge', compositeWidth / 2, 70);

    // Subhead
    ctx.fillStyle = '#C0C0C0';
    ctx.font = '20px sans-serif';
    ctx.fillText('Scan to book your treatment', compositeWidth / 2, 105);

    // White panel under QR for contrast
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(40, 130, size, size);

    // QR onto composite
    ctx.drawImage(qrCanvas, 40, 130, size, size);

    // Footer URL
    ctx.fillStyle = '#FFD700';
    ctx.font = 'bold 22px sans-serif';
    const displayUrl = url.replace(/^https?:\/\//, '');
    ctx.fillText(displayUrl, compositeWidth / 2, compositeHeight - 40);

    const link = document.createElement('a');
    link.download = 'eliteiv-qr-code.png';
    link.href = composite.toDataURL('image/png');
    link.click();
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // clipboard API may be unavailable; silent fallback
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'EliteIV Lounge',
          text: 'Book your IV therapy with EliteIV Lounge',
          url
        });
      } catch (_) {
        // user cancelled
      }
    } else {
      handleCopyUrl();
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="qr-code-page">
      <Navbar />

      <div className="max-w-5xl mx-auto px-6 py-12 lg:py-20">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-block px-4 py-1 rounded-full bg-yellow-400/10 border border-yellow-400/30 text-yellow-400 text-xs uppercase tracking-widest mb-4">
            Marketing Tools
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-yellow-400 mb-3">
            Your QR Code
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Print on flyers, business cards, vehicle wraps, or share digitally. Clients scan with
            their phone camera and land directly on your booking page.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* QR Preview */}
          <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-500/30 rounded-2xl p-8 flex flex-col items-center">
            <div
              ref={canvasRef}
              className="bg-white p-6 rounded-xl shadow-2xl"
              data-testid="qr-code-preview"
            >
              <QRCodeCanvas
                value={url}
                size={size}
                bgColor="#ffffff"
                fgColor="#0a0a0a"
                level="H"
                includeMargin={false}
                style={{ width: '280px', height: '280px' }}
              />
            </div>

            <p className="mt-6 text-center text-yellow-400 font-semibold break-all">
              {url.replace(/^https?:\/\//, '')}
            </p>
            <p className="text-xs text-gray-500 mt-1">Point your phone camera here to test</p>
          </div>

          {/* Controls */}
          <div className="space-y-5">
            <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-5">
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                <LinkIcon className="w-4 h-4 inline mr-2 text-yellow-400" />
                Destination URL
              </label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                placeholder="https://eliteiv.org"
                data-testid="qr-url-input"
              />
              <p className="text-xs text-gray-500 mt-2">
                Defaults to your live website. Change if you want the QR to link to a specific page
                (e.g., the booking section or your membership page).
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => setUrl(defaultUrl)}
                  className="text-xs text-yellow-400 hover:underline"
                  data-testid="qr-reset-url-btn"
                >
                  Reset to homepage
                </button>
                <span className="text-gray-700">|</span>
                <button
                  type="button"
                  onClick={() => setUrl(`${defaultUrl}/membership`)}
                  className="text-xs text-yellow-400 hover:underline"
                  data-testid="qr-membership-url-btn"
                >
                  Membership page
                </button>
                <span className="text-gray-700">|</span>
                <button
                  type="button"
                  onClick={() => setUrl(`${defaultUrl}/services`)}
                  className="text-xs text-yellow-400 hover:underline"
                  data-testid="qr-services-url-btn"
                >
                  Services page
                </button>
              </div>
            </div>

            <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-5">
              <label className="block text-sm font-semibold text-gray-300 mb-3">
                Download Size (Print Quality)
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'Small', value: 256, hint: 'Email / chat' },
                  { label: 'Medium', value: 512, hint: 'Flyers / cards' },
                  { label: 'Large', value: 1024, hint: 'Posters / vehicle' }
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSize(opt.value)}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      size === opt.value
                        ? 'bg-yellow-400 text-black border-yellow-400'
                        : 'bg-black/40 border-gray-700 text-gray-300 hover:border-yellow-400/50'
                    }`}
                    data-testid={`qr-size-${opt.label.toLowerCase()}-btn`}
                  >
                    <div className="text-sm font-bold">{opt.label}</div>
                    <div className="text-xs opacity-70 mt-0.5">{opt.hint}</div>
                    <div className="text-[10px] opacity-50 mt-1">{opt.value}px</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-5">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeBranding}
                  onChange={(e) => setIncludeBranding(e.target.checked)}
                  className="accent-yellow-400 w-5 h-5"
                  data-testid="qr-branding-toggle"
                />
                <div>
                  <div className="text-sm font-semibold text-gray-200">
                    Include branded frame
                  </div>
                  <div className="text-xs text-gray-500">
                    Adds "EliteIV Lounge" title, tagline, and URL below the QR — ready to print as-is.
                  </div>
                </div>
              </label>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleDownload}
                className="flex-1 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold py-4 rounded-xl hover:from-yellow-500 hover:to-yellow-700 transition-all flex items-center justify-center gap-2"
                data-testid="qr-download-btn"
              >
                <Download className="w-5 h-5" />
                Download PNG
              </button>

              <button
                type="button"
                onClick={handleCopyUrl}
                className="flex-1 bg-gray-800 border border-yellow-500/30 text-yellow-400 font-semibold py-4 rounded-xl hover:bg-gray-700 transition-all flex items-center justify-center gap-2"
                data-testid="qr-copy-url-btn"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                {copied ? 'Copied!' : 'Copy URL'}
              </button>

              <button
                type="button"
                onClick={handleShare}
                className="flex-1 bg-gray-800 border border-yellow-500/30 text-yellow-400 font-semibold py-4 rounded-xl hover:bg-gray-700 transition-all flex items-center justify-center gap-2"
                data-testid="qr-share-btn"
              >
                <Share2 className="w-5 h-5" />
                Share
              </button>
            </div>
          </div>
        </div>

        {/* Pro Tips */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: 'Print on business cards',
              body: 'Use Medium (512px) or Large (1024px) for crisp printing. The branded frame includes your URL as a fallback if scanning fails.'
            },
            {
              title: 'Add to your vehicle',
              body: 'Vehicle wrap or window decal — use the Large (1024px) size. Place at eye level so clients can scan while you load the truck.'
            },
            {
              title: 'Social media',
              body: 'Post the QR on Instagram or Facebook stories. Clients scan from one phone to another — instant booking, no typing.'
            }
          ].map((tip, idx) => (
            <div
              key={idx}
              className="bg-gradient-to-br from-yellow-400/5 to-transparent border border-yellow-400/20 rounded-xl p-5"
            >
              <h3 className="text-yellow-400 font-bold mb-2">{tip.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{tip.body}</p>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default QRCodePage;
